import api from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminFeature {
  feature_id: string;
  feature_key: string;
  display_name: string;
  description: string | null;
  feature_type: "binary" | "metered" | "metered_period" | "capacity";
  storage_unit: string | null;
  is_active: boolean;
}

export interface PlanFeatureConfig {
  feature_id: string;
  feature_key: string;
  feature_type?: "binary" | "metered" | "metered_period" | "capacity";
  is_enabled: boolean;
  included_quantity: number | null;
  enforcement_policy: "hard" | "soft";
}

export interface AdminPlan {
  plan_id: string;
  plan_key: string;
  display_name: string;
  description: string | null;
  marketing_bullets: string[];
  is_active: boolean;
  is_public: boolean;
  sort_order: number;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  platform_fee_percent_bps: number;
  platform_fee_fixed_minor_units: number;
  platform_fee_custom_allowed: boolean;
  live_payments_enabled: boolean;
  payments_required: boolean;
  deposit_min_fixed_minor_units: number;
  deposit_min_percent_bps: number;
  features?: PlanFeatureConfig[];
}

export type MissingPaymentMethodBehavior = "pause" | "cancel" | "create_invoice";

export interface EligibleTrialPlan {
  plan_id: string;
  plan_key: string;
  display_name: string;
  stripe_price_id: string;
  price_monthly: number | null;
  currency: string | null;
}

export interface AdminBillingConfiguration {
  billing_config_id: string;
  environment: string;
  trial_enabled: boolean;
  default_trial_plan_id: string;
  default_trial_days: number;
  payment_method_required: boolean;
  missing_payment_method_behavior: MissingPaymentMethodBehavior;
  trial_usage_overrides: Record<string, unknown>;
  version: number;
  updated_at: string | null;
}

export interface AdminBillingConfigurationResponse {
  configuration: AdminBillingConfiguration | null;
  eligible_trial_plans: EligibleTrialPlan[];
}

export interface AdminBillingConfigurationUpdate {
  trial_enabled: boolean;
  default_trial_plan_id: string;
  default_trial_days: number;
  payment_method_required: boolean;
  missing_payment_method_behavior: MissingPaymentMethodBehavior;
  trial_usage_overrides: Record<string, unknown>;
}

export interface BusinessSummary {
  business_id: string;
  name: string;
  email: string | null;
  plan_id: string | null;
  plan_key: string | null;
  plan_name: string | null;
  subscription_status: string | null;
}

export interface BusinessSubscription {
  subscription_id: string | null;
  plan_id: string | null;
  plan_key: string | null;
  status: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export interface FeatureOverride {
  override_id: string;
  feature_id: string;
  feature_key: string;
  feature_name: string;
  override_type: "force_enabled" | "force_disabled" | "custom_limit";
  override_limit_quantity: number | null;
  override_value?: number | null;
  reason?: string | null;
  expires_at: string | null;
  created_by?: string | null;
  created_at: string;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface EntitlementOverrideState {
  override_id: string;
  override_type: "force_enabled" | "force_disabled" | "custom_limit";
  value: number | null;
  expires_at: string | null;
  reason: string | null;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string | null;
}

export interface EntitlementFeatureState {
  allowed: boolean;
  feature_key: string;
  display_name: string;
  feature_type: "binary" | "metered_period" | "capacity";
  limit: number | null;
  used: number | null;
  remaining: number | null;
  reset_at: string | null;
  status: "available" | "near_limit" | "critical" | "exhausted" | "not_included" | "over_plan" | "force_disabled";
  reason_code: string;
  portal_message: string | null;
  cta: "upgrade" | "manage_billing" | "reduce_usage" | "disable_items" | "contact_support" | null;
  source: "plan" | "override";
  override: EntitlementOverrideState | null;
}

export interface AdminVoiceDebug {
  ok: true;
  provider: "vapi";
  enabled: boolean;
  status: string;
  vapi_assistant_id: string | null;
  vapi_phone_number_id: string | null;
  phone_number: string | null;
  model_provider: string | null;
  model: string | null;
  voice_provider: string | null;
  voice_id: string | null;
  tool_id_completeness: {
    configured_count: number;
    required_count: number;
    missing: string[];
  };
  sync_hash: string | null;
  last_synced_at: string | null;
  sync_error: string | null;
  generated_prompt_preview: string;
  first_message_preview: string;
  can_connect: boolean;
  can_sync: boolean;
  can_force_sync: boolean;
  can_disconnect: boolean;
}

export interface AdminVapiConnectResponse {
  ok: boolean;
  action: string;
  business_id: string;
  provider: "vapi";
  vapi_assistant_id: string | null;
  enabled: boolean;
  status: string;
  sync_hash: string | null;
  sync_error: string | null;
}

export interface AdminVapiToolRegistryItem {
  tool_name: string;
  vapi_tool_id: string | null;
  definition_version: string;
  server_url?: string | null;
  sync_status: "not_configured" | "synced" | "sync_error" | "stale" | string;
  sync_error: string | null;
  last_synced_at: string | null;
}

export interface AdminVapiToolRegistryDebug {
  ok: boolean;
  provider: "vapi";
  required_tool_count: number;
  configured_tool_count: number;
  missing_tools: string[];
  stale_tools: string[];
  errored_tools: string[];
  tools: AdminVapiToolRegistryItem[];
}

export interface AdminVapiToolRegistrySyncResponse {
  ok: boolean;
  provider: "vapi";
  created: string[];
  updated: string[];
  unchanged: string[];
  failed: string[];
  required_tool_count: number;
  configured_tool_count: number;
  missing_tools: string[];
  stale_tools: string[];
  sync_error: string | null;
}

export type VoicePhoneProvider =
  | "vapi_native"
  | "twilio_imported_vapi"
  | "twilio_inventory_only";

export type VoicePhoneInventoryStatus =
  | "available"
  | "reserved"
  | "assigned"
  | "cooldown"
  | "provider_error"
  | "retired";

export type VoicePhoneConnectionStatus =
  | "not_imported"
  | "import_pending"
  | "imported"
  | "attach_pending"
  | "attached"
  | "attach_failed";

export type VoicePhoneRoutingStatus =
  | "unknown"
  | "old_provider"
  | "mismatch"
  | "pending_verification"
  | "routed_to_vapi"
  | "routing_failed"
  | "rollback_failed";

export interface AdminVoicePhoneNumber {
  voice_phone_number_id: string;
  provider: VoicePhoneProvider;
  phone_number: string;
  country_code: string | null;
  region: string | null;
  region_source: string | null;
  twilio_sid?: string | null;
  vapi_phone_number_id?: string | null;
  capabilities: Record<string, unknown>;
  status: VoicePhoneInventoryStatus;
  vapi_connection_status: VoicePhoneConnectionStatus;
  assigned_business_id: string | null;
  assigned_business_name?: string | null;
  assigned_voice_provider_setting_id: string | null;
  cooldown_until: string | null;
  cooldown_reason: string | null;
  previous_business_id: string | null;
  safe_error: string | null;
  provider_error_code: string | null;
  provider_metadata?: Record<string, unknown>;
  routing_status?: VoicePhoneRoutingStatus;
  routing_target_url?: string | null;
  previous_routing?: Record<string, unknown>;
  last_routing_check_at?: string | null;
  last_routing_update_at?: string | null;
  last_synced_at: string | null;
  last_import_attempt_at: string | null;
  last_attach_attempt_at: string | null;
}

export interface AdminVoicePhoneNumbersParams {
  q?: string;
  status?: string;
  vapi_connection_status?: string;
  country_code?: string;
  region?: string;
  assigned?: boolean | null;
  limit?: number;
  offset?: number;
}

export interface AdminVoicePhoneNumbersResponse {
  ok: boolean;
  items: AdminVoicePhoneNumber[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

export interface AdminVoicePhoneSyncResponse {
  ok: boolean;
  provider: "twilio" | "vapi";
  created: number;
  updated: number;
  retired?: number;
  failed: Array<{ message: string; code?: string; phone_number?: string }>;
  warnings?: Array<{ message: string; code?: string; phone_number?: string }>;
}

export interface AdminBusinessSearchResult {
  business_id: string;
  name: string;
  status: string | null;
  owner_email: string | null;
  voice_provider: "vapi" | string | null;
  voice_config_status: string | null;
  vapi_assistant_id: string | null;
}

export interface AdminBusinessSearchResponse {
  ok: boolean;
  items: AdminBusinessSearchResult[];
}

export interface AdminVoicePhoneValidationIssue {
  code: string;
  message: string;
}

export interface AdminVoicePhoneAssignmentValidation {
  ok: boolean;
  can_assign: boolean;
  business: {
    business_id: string;
    vapi_assistant_id: string | null;
    voice_config_status: string | null;
  } | null;
  number: AdminVoicePhoneNumber | null;
  blocking_errors: AdminVoicePhoneValidationIssue[];
  warnings: AdminVoicePhoneValidationIssue[];
}

export interface AdminVoicePhoneOperationResponse {
  ok: boolean;
  voice_phone_number_id?: string;
  business_id?: string;
  phone_number?: string;
  vapi_phone_number_id?: string;
  status?: VoicePhoneInventoryStatus;
  vapi_connection_status?: VoicePhoneConnectionStatus;
  code?: string;
  message?: string;
  cooldown_until?: string;
  previous_business_id?: string;
}

export interface AdminVoicePhoneRoutingResponse {
  ok: boolean;
  voice_phone_number_id: string;
  phone_number?: string;
  routing_status?: VoicePhoneRoutingStatus;
  routing_target_url?: string | null;
  expected_routing_target_url?: string | null;
  previous_routing_available?: boolean;
  safe_error?: string | null;
  warnings?: string[];
  number?: AdminVoicePhoneNumber;
}

function isOperationResponse(value: unknown): value is AdminVoicePhoneOperationResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    "ok" in value &&
    typeof (value as { ok: unknown }).ok === "boolean"
  );
}

function extractOperationError(error: unknown): AdminVoicePhoneOperationResponse | null {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: unknown }).response === "object" &&
    (error as { response?: unknown }).response !== null
  ) {
    const response = (error as { response: { data?: unknown } }).response;
    return isOperationResponse(response.data) ? response.data : null;
  }
  return null;
}

function isRoutingResponse(value: unknown): value is AdminVoicePhoneRoutingResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    "ok" in value &&
    typeof (value as { ok: unknown }).ok === "boolean"
  );
}

function extractRoutingError(error: unknown): AdminVoicePhoneRoutingResponse | null {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: unknown }).response === "object" &&
    (error as { response?: unknown }).response !== null
  ) {
    const response = (error as { response: { data?: unknown } }).response;
    return isRoutingResponse(response.data) ? response.data : null;
  }
  return null;
}

export interface AdminVoicePhoneHistoryResponse {
  ok: boolean;
  assignments: Array<{
    business_id: string;
    business_name: string | null;
    assigned_at: string;
    assigned_by: string | null;
    released_at: string | null;
    release_reason: string | null;
  }>;
  events: Array<{
    event_type: string;
    message: string | null;
    metadata: Record<string, unknown>;
    created_at: string;
  }>;
}

// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------

export const fetchAdminFeatures = async (): Promise<AdminFeature[]> => {
  const res = await api.get("/platform-admin/features");
  return res.data.features ?? [];
};

export const updateAdminFeature = async (
  featureId: string,
  patch: Partial<Pick<AdminFeature, "display_name" | "description" | "is_active">>
): Promise<AdminFeature> => {
  const res = await api.patch(`/platform-admin/features/${featureId}`, patch);
  return res.data;
};

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

export const fetchAdminPlans = async (): Promise<AdminPlan[]> => {
  const res = await api.get("/platform-admin/plans");
  return res.data.plans ?? [];
};

export const createAdminPlan = async (
  body: Pick<AdminPlan, "plan_key" | "display_name"> &
    Partial<Pick<AdminPlan, "description" | "marketing_bullets" | "is_public" | "sort_order">>
): Promise<AdminPlan> => {
  const res = await api.post("/platform-admin/plans", body);
  return res.data;
};

export const updateAdminPlan = async (
  planId: string,
  patch: Partial<Pick<AdminPlan, "display_name" | "description" | "marketing_bullets" | "is_active" | "is_public" | "sort_order" | "stripe_product_id" | "stripe_price_id" | "platform_fee_percent_bps" | "platform_fee_fixed_minor_units" | "platform_fee_custom_allowed" | "live_payments_enabled" | "payments_required" | "deposit_min_fixed_minor_units" | "deposit_min_percent_bps">>
): Promise<AdminPlan> => {
  const res = await api.patch(`/platform-admin/plans/${planId}`, patch);
  return res.data;
};

export const replacePlanFeatures = async (
  planId: string,
  features: Pick<PlanFeatureConfig, "feature_id" | "is_enabled" | "enforcement_policy" | "included_quantity">[]
): Promise<{ plan_id: string; features: PlanFeatureConfig[] }> => {
  const res = await api.put(`/platform-admin/plans/${planId}/features`, { features });
  return res.data;
};

export const fetchAdminBillingConfiguration =
  async (): Promise<AdminBillingConfigurationResponse> => {
    const res = await api.get("/platform-admin/billing/configuration");
    return {
      configuration: res.data.configuration ?? null,
      eligible_trial_plans: res.data.eligible_trial_plans ?? [],
    };
  };

export const updateAdminBillingConfiguration = async (
  body: AdminBillingConfigurationUpdate
): Promise<{ configuration: AdminBillingConfiguration }> => {
  const res = await api.put("/platform-admin/billing/configuration", body);
  return res.data;
};

// ---------------------------------------------------------------------------
// Businesses
// ---------------------------------------------------------------------------

export const fetchAdminBusinesses = async (
  page = 1,
  pageSize = 50
): Promise<{ businesses: BusinessSummary[]; total: number; page: number; page_size: number }> => {
  const res = await api.get("/platform-admin/businesses", { params: { page, page_size: pageSize } });
  return res.data;
};

export const searchAdminBusinesses = async (
  q: string,
  limit = 20
): Promise<AdminBusinessSearchResult[]> => {
  const res = await api.get<AdminBusinessSearchResponse>(
    "/platform-admin/businesses/search",
    { params: { q, limit } }
  );
  return res.data.items ?? [];
};

export const getBusinessSubscription = async (
  businessId: string
): Promise<{
  business_id: string;
  business: { business_id: string; name: string; email?: string | null };
  subscription: BusinessSubscription;
  overrides: FeatureOverride[];
  features: EntitlementFeatureState[];
}> => {
  const res = await api.get(`/platform-admin/businesses/${businessId}/subscription`);
  return res.data;
};

export const updateBusinessSubscription = async (
  businessId: string,
  body: { plan_id?: string; status?: string; current_period_start?: string; current_period_end?: string }
): Promise<BusinessSubscription> => {
  const res = await api.put(`/platform-admin/businesses/${businessId}/subscription`, body);
  return res.data.subscription;
};

export const createBusinessOverride = async (
  businessId: string,
  body: {
    feature_id: string;
    override_type: "force_enabled" | "force_disabled" | "custom_limit";
    override_limit_quantity?: number | null;
    override_value?: number | null;
    expires_at?: string | null;
    reason: string;
  }
): Promise<FeatureOverride> => {
  const res = await api.post(`/platform-admin/businesses/${businessId}/overrides`, body);
  return res.data.override;
};

export const deleteBusinessOverride = async (businessId: string, overrideId: string): Promise<void> => {
  await api.delete(`/platform-admin/businesses/${businessId}/overrides/${overrideId}`);
};

// ---------------------------------------------------------------------------
// Voice
// ---------------------------------------------------------------------------

export const getAdminVoiceDebug = async (
  businessId: string
): Promise<AdminVoiceDebug> => {
  const res = await api.get(
    `/platform-admin/businesses/${businessId}/voice/vapi/debug`
  );
  return res.data;
};

export const connectAdminVapiAssistant = async (
  businessId: string,
  force = false
): Promise<AdminVapiConnectResponse> => {
  const res = await api.post(
    `/platform-admin/businesses/${businessId}/voice/vapi/connect`,
    { force }
  );
  return res.data;
};

export const disconnectAdminVapiAssistant = async (
  businessId: string,
  reason?: string
): Promise<AdminVapiConnectResponse> => {
  const res = await api.post(
    `/platform-admin/businesses/${businessId}/voice/vapi/disconnect`,
    reason ? { disconnect_reason: reason } : {}
  );
  return res.data;
};

export const fetchAdminVapiToolRegistryDebug =
  async (): Promise<AdminVapiToolRegistryDebug> => {
    const res = await api.get<AdminVapiToolRegistryDebug>(
      "/platform-admin/voice/vapi/tools/debug"
    );
    return res.data;
  };

export const syncAdminVapiTools = async (
  force = false
): Promise<AdminVapiToolRegistrySyncResponse> => {
  const res = await api.post<AdminVapiToolRegistrySyncResponse>(
    "/platform-admin/voice/vapi/tools/sync",
    { force }
  );
  return res.data;
};

// ---------------------------------------------------------------------------
// Voice Phone Numbers
// ---------------------------------------------------------------------------

export const fetchAdminVoicePhoneNumbers = async (
  params: AdminVoicePhoneNumbersParams = {}
): Promise<AdminVoicePhoneNumbersResponse> => {
  const res = await api.get<AdminVoicePhoneNumbersResponse>(
    "/platform-admin/voice/phone-numbers",
    { params }
  );
  return res.data;
};

export const syncAdminTwilioPhoneNumbers = async (
  force = false
): Promise<AdminVoicePhoneSyncResponse> => {
  const res = await api.post<AdminVoicePhoneSyncResponse>(
    "/platform-admin/voice/phone-numbers/sync-twilio",
    { force }
  );
  return res.data;
};

export const syncAdminVapiPhoneNumbers = async (
  force = false
): Promise<AdminVoicePhoneSyncResponse> => {
  const res = await api.post<AdminVoicePhoneSyncResponse>(
    "/platform-admin/voice/phone-numbers/sync-vapi",
    { force }
  );
  return res.data;
};

export const validateAdminVoicePhoneAssignment = async (
  voicePhoneNumberId: string,
  body: {
    business_id: string;
    confirm_region_warning?: boolean;
    allow_cooldown_override?: boolean;
  }
): Promise<AdminVoicePhoneAssignmentValidation> => {
  const res = await api.post<AdminVoicePhoneAssignmentValidation>(
    `/platform-admin/voice/phone-numbers/${voicePhoneNumberId}/validate-assignment`,
    body
  );
  return res.data;
};

export const assignAdminVoicePhoneNumber = async (
  voicePhoneNumberId: string,
  body: {
    business_id: string;
    confirm_region_warning?: boolean;
    allow_cooldown_override?: boolean;
    reason?: string;
  }
): Promise<AdminVoicePhoneOperationResponse> => {
  try {
    const res = await api.post<AdminVoicePhoneOperationResponse>(
      `/platform-admin/voice/phone-numbers/${voicePhoneNumberId}/assign`,
      body
    );
    return res.data;
  } catch (error) {
    const operationError = extractOperationError(error);
    if (operationError) return operationError;
    throw error;
  }
};

export const releaseAdminVoicePhoneNumber = async (
  voicePhoneNumberId: string,
  body: {
    reason: string;
    cooldown_days?: number;
  }
): Promise<AdminVoicePhoneOperationResponse> => {
  try {
    const res = await api.post<AdminVoicePhoneOperationResponse>(
      `/platform-admin/voice/phone-numbers/${voicePhoneNumberId}/release`,
      body
    );
    return res.data;
  } catch (error) {
    const operationError = extractOperationError(error);
    if (operationError) return operationError;
    throw error;
  }
};

export const fetchAdminVoicePhoneNumberHistory = async (
  voicePhoneNumberId: string
): Promise<AdminVoicePhoneHistoryResponse> => {
  const res = await api.get<AdminVoicePhoneHistoryResponse>(
    `/platform-admin/voice/phone-numbers/${voicePhoneNumberId}/history`
  );
  return res.data;
};

export const inspectAdminVoicePhoneRouting = async (
  voicePhoneNumberId: string
): Promise<AdminVoicePhoneRoutingResponse> => {
  try {
    const res = await api.post<AdminVoicePhoneRoutingResponse>(
      `/platform-admin/voice/phone-numbers/${voicePhoneNumberId}/routing/inspect`,
      {}
    );
    return res.data;
  } catch (error) {
    const routingError = extractRoutingError(error);
    if (routingError) return routingError;
    throw error;
  }
};

export const cutoverAdminVoicePhoneRouting = async (
  voicePhoneNumberId: string,
  body: { confirm_region_warning?: boolean } = {}
): Promise<AdminVoicePhoneRoutingResponse> => {
  try {
    const res = await api.post<AdminVoicePhoneRoutingResponse>(
      `/platform-admin/voice/phone-numbers/${voicePhoneNumberId}/routing/cutover`,
      { confirm_region_warning: body.confirm_region_warning ?? false }
    );
    return res.data;
  } catch (error) {
    const routingError = extractRoutingError(error);
    if (routingError) return routingError;
    throw error;
  }
};

export const verifyAdminVoicePhoneRouting = async (
  voicePhoneNumberId: string
): Promise<AdminVoicePhoneRoutingResponse> => {
  try {
    const res = await api.post<AdminVoicePhoneRoutingResponse>(
      `/platform-admin/voice/phone-numbers/${voicePhoneNumberId}/routing/verify`,
      {}
    );
    return res.data;
  } catch (error) {
    const routingError = extractRoutingError(error);
    if (routingError) return routingError;
    throw error;
  }
};

export const rollbackAdminVoicePhoneRouting = async (
  voicePhoneNumberId: string,
  body: { reason?: string } = {}
): Promise<AdminVoicePhoneRoutingResponse> => {
  try {
    const res = await api.post<AdminVoicePhoneRoutingResponse>(
      `/platform-admin/voice/phone-numbers/${voicePhoneNumberId}/routing/rollback`,
      body.reason ? { reason: body.reason } : {}
    );
    return res.data;
  } catch (error) {
    const routingError = extractRoutingError(error);
    if (routingError) return routingError;
    throw error;
  }
};
